import React, {useEffect, useRef, ReactElement} from 'react';

import {Locale, useLocale} from '../../../RootProvider/LocaleContext';
import {TrustpilotWidgetVariant, trustpilotWidgetVariantMapping} from './trustpilotWidgetVariantConfig';

import './TrustpilotWidget.scss';

interface Props {
  variant?: TrustpilotWidgetVariant;
  theme?: 'light' | 'dark';
}

function TrustpilotWidget({variant = TrustpilotWidgetVariant.MINI, theme = 'light'}: Props): ReactElement {
  const locale = useLocale();
  const trustpilotNamespace = typeof window === 'undefined' ? undefined : window.Trustpilot;
  // If unhandled variant is given, widget will resolve to mini
  const ref = useRef<HTMLDivElement | null>(null);
  const trustpilotHref = `https://${locale === Locale.nl ? 'nl' : 'uk'}.trustpilot.com/review/fundingoptions.com`;

  useEffect(() => {
    // Ensures there is only one trustpilot script on the page (in case there are multiple widgets on the page)
    const trustpilotScriptSrc = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    let trustpilotScriptEl = document.querySelector(`script[src="${trustpilotScriptSrc}"]`) as HTMLScriptElement;

    if (!trustpilotScriptEl) {
      trustpilotScriptEl = document.createElement('script');
      trustpilotScriptEl.type = 'text/javascript';
      trustpilotScriptEl.src = trustpilotScriptSrc;
      trustpilotScriptEl.async = true;
      document.head.appendChild(trustpilotScriptEl);
    }
    if (trustpilotNamespace && ref.current) {
      // Load this current widget
      ref.current.innerHTML = `<a href="${trustpilotHref}" target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>`;

      trustpilotNamespace.loadFromElement(ref.current);
    }
  }, [trustpilotNamespace, variant, theme]);

  const getTemplateId = () => {
    return trustpilotWidgetVariantMapping[variant].templateId;
  };

  const getStyleHeight = () => {
    return trustpilotWidgetVariantMapping[variant].styleHeight;
  };

  const getTrustpilotLocale = () => {
    switch (locale) {
      case Locale.nl:
        return 'nl-NL';
      case Locale.uk:
        return 'en-GB';
      default:
        return 'en-GB';
    }
  };

  const getTrustpilotLanguageValue = () => {
    switch (locale) {
      case Locale.nl:
        return 'nl';
      case Locale.uk:
        return 'en';
      default:
        return 'en-GB';
    }
  };

  return (
    <div
      ref={ref}
      // the css class trustpilot-widget must not be changed as the trustpilot script will look for this element
      // to mount its widget in.
      className={`trustpilot-widget trustpilot-widget--${variant}`}
      data-review-languages={getTrustpilotLanguageValue()}
      data-locale={getTrustpilotLocale()}
      data-template-id={getTemplateId()}
      data-businessunit-id="54c8d9930000ff00057d09a3"
      data-style-height={getStyleHeight()}
      data-style-width="100%"
      data-theme={theme}
      data-stars="4,5"
    >
      <a href={trustpilotHref} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
}

export default TrustpilotWidget;
